import {
  BlueBirdTheme,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@kitbird/core";
import {
  CSSProperties, PropsWithChildren, useMemo,
} from "react";
import { ColumnsI, SortI, TableI } from "./TableProps";
import { ReactComponent as EmptyTable } from "../../assets/images/empty_table.svg";
import { ReactComponent as SortAscend } from "../../assets/icons/sort_ascend.svg";
import { ReactComponent as SortDescend } from "../../assets/icons/sort_descend.svg";
import { ReactComponent as SortDefault } from "../../assets/icons/sort_default.svg";
import { CustomTable, CustomTableRow, TableMessage } from "./Table.style";

const {
  black,
  gray,
} = BlueBirdTheme.colors;

interface CustomTableCellSortI extends PropsWithChildren {
  name: string;
  sortConfig?: SortI;
  style?: CSSProperties;
  sticky?: "left" | "right";
}

const CustomTableCellSort = ({
  name = "",
  sortConfig = {
    sortBy: "",
    sortType: "DEFAULT",
  },
  style,
  sticky,
  children,
}: CustomTableCellSortI) => {
  const {
    sortBy,
    sortType,
    onSort,
  } = sortConfig;
  return <TableCell
    onClick={() => {
      let newVal = sortType;
      if (sortType === "DEFAULT" || sortBy !== name) newVal = "ASC";
      else if (sortType === "ASC") newVal = "DSC";
      else if (sortType === "DSC") newVal = "DEFAULT";
      if (onSort) onSort(name, newVal);
    }}
    className="prevent-select"
    style={{ cursor: "pointer", ...style }}
    sticky={sticky}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      {children}
      {(sortBy === name && sortType === "ASC") && <SortAscend />}
      {(sortBy === name && sortType === "DSC") && <SortDescend />}
      {(sortBy !== name || sortType === "DEFAULT") && <SortDefault />}
    </div>
  </TableCell>;
};

const Table = ({
  dataSource,
  columns,
  hiddenColumnKeys,
  keyId = "id",
  selectedRowKeys = [],
  footer,
  sortConfig,
  withNumber,
  hoverable,
  onClickRow,
  emptyMessage = "Maaf, Data yang anda cari belum tersedia",
}: TableI) => {
  const numberedColumns: ColumnsI = useMemo(() => {
    if (withNumber && columns) {
      return [{
        title: "No.", dataIndex: "no", key: "no", width: "50px",
      }, ...columns];
    }
    return columns ? [...columns] : [];
  }, [withNumber, columns]);

  const numberedData: Record<string, any>[] = useMemo(() => {
    if (dataSource && (withNumber || selectedRowKeys.length)) {
      const tempData = dataSource?.map((item, i) => {
        const insertData = {
          no: `${i + 1}.`,
          isSelected: selectedRowKeys.includes(item[keyId]),
        };
        return {
          ...insertData,
          ...item,
        };
      });
      return tempData;
    }
    return dataSource ? [...dataSource] : [];
  }, [withNumber, dataSource, columns, selectedRowKeys]);

  return (dataSource?.length !== 0
    ? <CustomTable $hoverable={hoverable || !!onClickRow}>
      <TableHead sticky>
        <TableRow>
          {numberedColumns?.map((col) => ((col && !hiddenColumnKeys?.includes(col.key)) && (
            col.sort
              ? <CustomTableCellSort
                key={`head-table-cell-sort-${col.key}`}
                name={col.key}
                sortConfig={sortConfig}
                sticky={col.sticky}
                style={{ textTransform: "none", ...col.headerStyle, width: col.width }}
              >
                <Typography level={7} fontWeight="bold" color={black}>{col.title}</Typography>
              </CustomTableCellSort>
              : <TableCell
                key={`head-table-cell-${col.key}`}
                sticky={col.sticky}
                style={{ textTransform: "none", ...col.headerStyle, width: col.width }}
              >
                <Typography level={7} fontWeight="bold" color={black}>{col.title}</Typography>
              </TableCell>
          )
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dataSource && numberedData.map((item, i: number) => (
          <CustomTableRow
            key={`body-table-row-${i}`}
            isEven={!((i + 1) % 2)}
            $isSelected={item.isSelected}
            onClick={onClickRow
              ? (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => onClickRow(item, e)
              : undefined
            }
          >
            {numberedColumns?.map((col) => (col && !hiddenColumnKeys?.includes(col.key)) && (
              <TableCell key={`body-table-cell-${col.key}`} style={col.style} sticky={col.sticky}>
                <Typography level={7} fontWeight="medium" color={gray.subtitle}>
                  {col.render
                    ? col.render(item[col.dataIndex], item, i)
                    : item[col.dataIndex]
                  }
                </Typography>
              </TableCell>
            ))}
          </CustomTableRow>
        ))}
      </TableBody>
      {footer}
    </CustomTable>
    : <TableMessage>
      <EmptyTable />
      <Typography level={4} fontWeight="medium">{emptyMessage}</Typography>
    </TableMessage>
  );
};

export default Table;
